/**
 * Standardised timestamp formatting (DD/MMM/YYYY) used everywhere the
 * SPA shows a date, so a user isn't reading DD/MM/YYYY in one screen
 * and MM/DD/YYYY in another depending on which `toLocaleString()`
 * call happened to render it — the previous per-screen ad hoc
 * `toLocaleString()`/`toLocaleDateString()` calls each followed the
 * browser's locale, which is exactly the ambiguity this replaces.
 */
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/**
 * An all-digit STRING is epoch milliseconds, not a date string.
 * `new Date("1755158400000")` is `Invalid Date` — the string form
 * never reaches the numeric constructor overload — so a caller
 * handing over a stringified epoch got `—` for every row rather than
 * a wrong-looking date it could have noticed. The alert view model
 * carries its timestamp in exactly that form (`adapters.ts::
 * adaptAlert` stringifies `openedAtMs`), and `AlertBell.tsx` already
 * applies this same rule to the same field; a screen formatting it
 * must not disagree with the bell about what the value means.
 */
function toDate(value: string | number | Date): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const epochMs =
    typeof value === 'string' && /^\d+$/.test(value.trim()) ? Number(value) : value;
  const d = new Date(epochMs);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `22/Jul/2026` */
export function formatDate(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const d = toDate(value);
  if (!d) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  return `${day}/${MONTHS[d.getMonth()]}/${d.getFullYear()}`;
}

/** `22/Jul/2026, 14:35` */
export function formatDateTime(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const d = toDate(value);
  if (!d) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${formatDate(d)}, ${hh}:${mm}`;
}

/**
 * `45s` under a minute (no point padding a bare seconds count with
 * zero hours/minutes), `12:34` under an hour, `1:02:34` at/above one
 * hour. Takes whole seconds; CSV/tabular exports should keep the raw
 * `durationSec` number instead of this formatted string.
 */
export function formatDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined || Number.isNaN(totalSeconds)) return '—';
  const s = Math.max(0, Math.round(totalSeconds));
  if (s < 60) return `${s}s`;
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${mm}:${pad(ss)}`;
}
